import styles from './mainView.module.css';

type IFloorHoistBadgeProps = {
  count: number;
  /** Drives the static-vs-moving glyph variant (mirrors trusstor's
   *  `elevatorIcon` vs `elevatorStaticIcon`). The glyph differs by an
   *  extra clock-marker badge that the static variant overlays. */
  isMoving: boolean;
  onClick: () => void;
  ariaLabel: string;
};

/**
 * Per-floor hoist badge that hangs in the side rail next to the building.
 * Mirrors trusstor's `HoistIcon.tsx`:
 *   - 25×25 dark circle with two stacked white triangles (up/down arrows)
 *     suggesting an elevator.
 *   - 15×15 white avatar pinned top-right with the count when more than one
 *     hoist serves the same fixed floor.
 *   - The static variant (no live movement) adds a small clock-marker.
 */
export function FloorHoistBadge({ count, isMoving, onClick, ariaLabel }: IFloorHoistBadgeProps) {
  return (
    <button
      type="button"
      className={styles.hoistBadge}
      data-moving={isMoving || undefined}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {/* The dark disc uses `currentColor`; the wrapping CSS rule sets
          `color: var(--hoist-glyph-fill)`, which keeps all hex values
          inside tokens.css. */}
      <svg
        viewBox="0 0 25 25"
        width="25"
        height="25"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="10" cy="15" r="10" fill="currentColor" />
        {/* Up + down triangles (lifted from trusstor's elevator_icon.svg). */}
        <path
          d="M12.9391 12.2604L10.0702 17.2169c-.0459.0793-.0701.1693-.0702.2609 0 .0916.024.1817.0697.2611.0458.0794.1117.1453.191.1911.0794.0459.1694.07.261.07h5.7378c.0916 0 .1816-.0241.261-.07.0793-.0458.1452-.1117.191-.1911.0458-.0794.0698-.1695.0697-.2611 0-.0916-.0242-.1816-.0701-.2609L13.8422 12.2604c-.0458-.0792-.1116-.1449-.1909-.1906-.0793-.0457-.1691-.0698-.2606-.0698-.0915 0-.1814.0241-.2607.0698-.0792.0457-.1451.1114-.1909.1906z"
          fill="white"
        />
        <path
          d="M7.10265 17.7396L9.97156 12.7831c.0459-.0793.0701-.1693.0701-.2609 0-.0916-.024-.1817-.0697-.2611-.0458-.0794-.1117-.1453-.191-.1911-.0794-.0459-.1694-.07-.261-.07H3.78217c-.0916 0-.1816.0241-.261.07-.0793.0458-.1452.1117-.191.1911-.0458.0794-.0698.1695-.0697.2611 0 .0916.0242.1816.0701.2609L6.19951 17.7396c.0458.0792.1117.1449.1905.1906.0793.0457.1692.0698.2606.0698.0915 0 .1814-.0241.2607-.0698.0793-.0457.1451-.1114.1909-.1906z"
          fill="white"
        />
        {!isMoving && (
          <g>
            {/* Static-only clock marker overlay — strokes pick up
                `currentColor` so the marker stays in sync with the
                disc fill via tokens. */}
            <circle cx="17.5" cy="7.5" r="6" fill="white" />
            <path
              d="M17.5 5.09v2.41h2.41"
              stroke="currentColor"
              strokeWidth="0.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <circle
              cx="17.5"
              cy="7.5"
              r="3.78"
              stroke="currentColor"
              strokeWidth="0.7"
              fill="none"
            />
          </g>
        )}
      </svg>
      {count > 1 && (
        <span className={styles.hoistBadgeCount}>{count}</span>
      )}
    </button>
  );
}
